import { registerLocaleData } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';

import { select, Store } from '@ngrx/store';
import { map } from 'rxjs/operators';
import { AppState } from 'src/app/app.state';
import { selectMerchantDefaultLanguage } from 'src/app/core/merchant-details/merchant-details.selectors';
import { DOCUMENT } from 'src/app/feature/injection-token/document';
import { WINDOW } from 'src/app/feature/injection-token/window';
import { validString } from 'src/app/shared/utilities/types.utils';
import { routeToDefaultLang } from 'src/app/shared/utilities/window.utils';
import { fetchLocaleFile, fetchLocaleFileSuccess } from './locale.actions';
import { LocaleMappingService } from '../services/locale-mapping.service';

/**
 * Handles any sort of requesting of locale-related information.
 */
@Injectable()
export class LocaleEffects {
	/**
	 * Loads the global locale file.
	 */
	fetchLocaleFile$ = createEffect(
		() =>
			this._actions$.pipe(
				ofType(fetchLocaleFile),
				map(({ payload }) => {
					const locale = !validString(payload) ? 'en-US' : this.localeMapper.localeConvert(payload);
					// en is always loaded by default in Angular
					if (['en', 'en-US'].includes(locale)) {
						this._setLocaleMetaData(locale);
						this._store$.dispatch(fetchLocaleFileSuccess());
					} else {
						try {
							this._getLocaleImport(locale).then((localeData) => {
								registerLocaleData(localeData.default, locale);
								this._setLocaleMetaData(locale);
								this._store$.dispatch(fetchLocaleFileSuccess());
							});
						} catch (error) {
							this._store$.pipe(select(selectMerchantDefaultLanguage)).subscribe((defaultLanguage) => {
								routeToDefaultLang(this._window, defaultLanguage);
							});
						}
					}
				})
			),
		{ dispatch: false }
	);

	/**
	 * Our constructor
	 * @param _document the document
	 * @param _window Window Object
	 * @param _actions$ The actions stream.
	 * @param _store$ the store
	 */
	constructor(
		@Inject(DOCUMENT) private _document: Document,
		@Inject(WINDOW) private _window: Window,
		private _actions$: Actions,
		private _store$: Store<AppState>,
		private localeMapper: LocaleMappingService
	) {}

	private _setLocaleMetaData(locale: string): void {
		this._document.documentElement.lang = locale;
	}

	private _getLocaleImport(locale: string): Promise<any> {
		return import(`../../../../node_modules/@angular/common/locales/${locale}.mjs`);
	}
}
